import { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = "";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true }); } catch {}
    setUser(null);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = useCallback(async (userData) => {
    setUser(userData);
    // Fetch full user data with role & permissions
    try {
      const res = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {}
  }, []);

  // Permission helpers
  const hasPermission = useCallback((section, action = "view") => {
    if (!user || !user.permissions) return false;
    const perms = user.permissions[section];
    if (!perms) return false;
    if (perms[action]) return true;
    // For inventory: grant access if ANY sub-page permission is enabled
    if (section === "inventory") {
      const suffix = `_${action}`;
      return Object.keys(perms).some(k => k.endsWith(suffix) && perms[k]);
    }
    return false;
  }, [user]);

  const hasSubPermission = useCallback((section, subPermKey, action = "view") => {
    if (!user || !user.permissions) return false;
    const perms = user.permissions[section];
    if (!perms) return false;
    // Top-level master switch ON = access to everything
    if (perms[action]) return true;
    return !!perms[`${subPermKey}_${action}`];
  }, [user]);

  const isMasterAdmin = user?.role === "master_admin";
  const isAdmin = user?.role === "admin" || isMasterAdmin;
  const isStaff = user?.role === "staff";

  return (
    <AuthContext.Provider value={{
      user, loading, fetchUser, checkAuth: fetchUser, logout, login,
      hasPermission, hasSubPermission, isMasterAdmin, isAdmin, isStaff,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
